import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Zones() {
  const { selectedId } = useProperty();
  const [zones, setZones] = useState([]);
  const [areas, setAreas] = useState([]);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: "", id: null });
  const [areaForm, setAreaForm] = useState({ name: "", zone_id: "", id: null });

  const load = useCallback(async () => {
    if (!selectedId) return;
    const [z, a] = await Promise.all([
      api.get(`/zones?property_id=${selectedId}`),
      api.get(`/areas?property_id=${selectedId}`),
    ]);
    setZones(z.data); setAreas(a.data);
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  const submitZone = async () => {
    try {
      if (zoneForm.id) await api.put(`/zones/${zoneForm.id}`, { property_id: selectedId, name: zoneForm.name });
      else await api.post(`/zones`, { property_id: selectedId, name: zoneForm.name });
      toast.success("Saved");
      setZoneOpen(false); setZoneForm({ name: "", id: null });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const submitArea = async () => {
    try {
      if (areaForm.id) await api.put(`/areas/${areaForm.id}`, { property_id: selectedId, zone_id: areaForm.zone_id, name: areaForm.name });
      else await api.post(`/areas`, { property_id: selectedId, zone_id: areaForm.zone_id, name: areaForm.name });
      toast.success("Saved");
      setAreaOpen(false); setAreaForm({ name: "", zone_id: "", id: null });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!selectedId) return <div className="text-zinc-500">Select a property to manage zones.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <span className="label-overline">Property Layout</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Zones & Areas</h1>
        </div>
        <Dialog open={zoneOpen} onOpenChange={setZoneOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setZoneForm({ name: "", id: null })} className="bg-zinc-950 hover:bg-zinc-800" data-testid="add-zone-button">
              <Plus className="w-4 h-4 mr-2" /> Add Zone
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{zoneForm.id ? "Edit Zone" : "New Zone"}</DialogTitle></DialogHeader>
            <div>
              <Label>Zone name</Label>
              <Input value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} data-testid="zone-name-input" />
            </div>
            <DialogFooter><Button onClick={submitZone} className="bg-zinc-950" data-testid="save-zone-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {zones.map((z) => {
          const zoneAreas = areas.filter((a) => a.zone_id === z.id);
          return (
            <div key={z.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`zone-card-${z.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-overline">Zone</div>
                  <h3 className="font-display text-xl font-bold tracking-tight mt-1">{z.name}</h3>
                  <div className="text-xs text-zinc-500 mt-0.5">{zoneAreas.length} area(s)</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setZoneForm({ name: z.name, id: z.id }); setZoneOpen(true); }} className="p-2 hover:bg-zinc-100 rounded" data-testid={`edit-zone-${z.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={async () => { if (window.confirm("Delete zone and its areas?")) { await api.delete(`/zones/${z.id}`); toast.success("Deleted"); load(); } }}
                    className="p-2 hover:bg-red-50 hover:text-red-600 rounded"
                    data-testid={`delete-zone-${z.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {zoneAreas.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-zinc-50 px-3 py-2 rounded text-sm" data-testid={`area-${a.id}`}>
                    <span>{a.name}</span>
                    <div className="flex gap-1">
                      <button onClick={() => { setAreaForm({ name: a.name, zone_id: a.zone_id, id: a.id }); setAreaOpen(true); }} className="p-1 hover:bg-white rounded">
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button onClick={async () => { await api.delete(`/areas/${a.id}`); load(); }} className="p-1 hover:bg-white text-red-600 rounded">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => { setAreaForm({ name: "", zone_id: z.id, id: null }); setAreaOpen(true); }}
                  className="w-full text-sm text-zinc-600 border border-dashed border-zinc-300 rounded py-2 hover:bg-zinc-50"
                  data-testid={`add-area-to-${z.id}`}
                >
                  + Add Area
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={areaOpen} onOpenChange={setAreaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{areaForm.id ? "Edit Area" : "New Area"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Area name</Label>
              <Input value={areaForm.name} onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })} data-testid="area-name-input" />
            </div>
            <div>
              <Label>Zone</Label>
              <Select value={areaForm.zone_id} onValueChange={(v) => setAreaForm({ ...areaForm, zone_id: v })}>
                <SelectTrigger data-testid="area-zone-select"><SelectValue placeholder="Select zone" /></SelectTrigger>
                <SelectContent>
                  {zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submitArea} className="bg-zinc-950" data-testid="save-area-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
